import { useMemo } from 'react';
import moment from 'moment';
import i18n from 'i18n-js';

import { UsePriorityAdjustmentProps, PriorityAdjustmentResult } from '@v2/pages/planboard/types';

const usePriorityAdjustment = ({
  scheduledStartDate,
  scheduledStartDateOverride,
  expectedStartOn,
  hasPriorityOverride,
}: UsePriorityAdjustmentProps): PriorityAdjustmentResult => {
  return useMemo(() => {
    const hasOverride = !!hasPriorityOverride && !!scheduledStartDateOverride && !!scheduledStartDate;

    // Calculate priority adjustment (override vs original scheduled)
    let isPriorityIncreased = false;
    let isPriorityDecreased = false;
    let priorityDays = 0;
    let priorityHours = 0;
    let priorityDisplayText = '';

    if (hasOverride) {
      const overrideDate = moment(scheduledStartDateOverride);
      const scheduledDate = moment(scheduledStartDate);
      const diffHours = overrideDate.diff(scheduledDate, 'hours');
      const diffDays = overrideDate.diff(scheduledDate, 'days');

      // Priority increased = override is earlier than scheduled (moved up in queue)
      // Priority decreased = override is later than scheduled (moved down in queue)
      isPriorityIncreased = diffHours < 0;
      isPriorityDecreased = diffHours > 0;

      priorityHours = Math.abs(diffHours);
      priorityDays = Math.abs(diffDays);

      // Display hours if less than 24 hours, otherwise days
      if (priorityHours < 24) {
        priorityDisplayText = `${priorityHours}${i18n.t('h')}`;
      } else {
        priorityDisplayText = priorityDays === 1
          ? `${priorityDays} ${i18n.t('day')}`
          : `${priorityDays} ${i18n.t('days')}`;
      }
    }

    // Calculate days difference (scheduled vs expected)
    let daysDifference: number | null = null;
    let isAhead = false;
    let isBehind = false;
    let daysDisplayText = '-';

    if (scheduledStartDate && expectedStartOn) {
      daysDifference = moment(scheduledStartDate).diff(moment(expectedStartOn), 'days');
      isAhead = daysDifference > 0;
      isBehind = daysDifference < 0;
      daysDisplayText = Math.abs(daysDifference).toString();
    }

    return {
      hasPriorityOverride: hasOverride,
      isPriorityIncreased,
      isPriorityDecreased,
      priorityDays,
      priorityHours,
      priorityDisplayText,
      daysDifference,
      isAhead,
      isBehind,
      daysDisplayText,
    };
  }, [scheduledStartDate, scheduledStartDateOverride, expectedStartOn, hasPriorityOverride]);
};

export default usePriorityAdjustment;
